import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Pencil as EditIcon, Trash2 as DeleteIcon, Play as PlayArrowIcon, Pause as PauseIcon } from 'lucide-react';
import { Button, Chip, ShellHeader } from '@coordio/ui';
import { FormSection, InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';
import { scheduleService } from '../services/scheduleService';
import { useNotificationStore } from '../store/notificationStore';
import { ScheduleExecutionHistory } from '../components/schedules/ScheduleExecutionHistory';
import { useConfirmDialog } from '../components/common/ConfirmDialog';
import type { ScheduleDto, ScheduleType } from '../types/schedule';
import { ScheduleTypes } from '../types/schedule';

/**
 * Schedule detail page component.
 */
export const ScheduleDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotificationStore();
  const confirm = useConfirmDialog();

  const [schedule, setSchedule] = useState<ScheduleDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadSchedule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadSchedule = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await scheduleService.getSchedule(id);
      setSchedule(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load schedule');
      showNotification(err.message || 'Failed to load schedule', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    const ok = await confirm({
      title: 'Delete schedule',
      message: 'Are you sure you want to delete this schedule?',
      confirmText: 'Delete',
    });
    if (!ok) return;

    try {
      await scheduleService.deleteSchedule(id);
      showNotification('Schedule deleted successfully', 'success');
      navigate('/schedules');
    } catch (err: any) {
      showNotification(err.message || 'Failed to delete schedule', 'error');
    }
  };

  const handleToggleActive = async () => {
    if (!id || !schedule) return;

    try {
      if (schedule.isActive) {
        await scheduleService.disableSchedule(id);
        showNotification('Schedule disabled successfully', 'success');
      } else {
        await scheduleService.enableSchedule(id);
        showNotification('Schedule enabled successfully', 'success');
      }
      loadSchedule();
    } catch (err: any) {
      showNotification(err.message || 'Failed to update schedule', 'error');
    }
  };

  const formatScheduleType = (type: ScheduleType): string => {
    switch (type) {
      case ScheduleTypes.Cron:
        return 'Cron';
      case ScheduleTypes.Interval:
        return 'Interval';
      case ScheduleTypes.OneTime:
        return 'One-Time';
      default:
        return type;
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <div className="schedule-detail" data-testid="schedule-form-page">
      <ShellHeader
        data-testid="schedule-header"
        title={schedule?.name || 'Schedule'}
        description="Schedule configuration and execution history."
        actions={
          schedule ? (
            <>
              <Button data-testid="schedule-toggle-btn" variant="outline" size="sm" className="gap-2" onClick={handleToggleActive}>
                {schedule.isActive ? <PauseIcon className="h-4 w-4" /> : <PlayArrowIcon className="h-4 w-4" />}
                {schedule.isActive ? 'Disable' : 'Enable'}
              </Button>
              <Button data-testid="edit-schedule-btn" variant="outline" size="sm" className="gap-2" onClick={() => navigate(`/schedules/${schedule.id}/edit`)}>
                <EditIcon className="h-4 w-4" />
                Edit
              </Button>
              <Button data-testid="delete-schedule-btn" variant="destructive" size="sm" className="gap-2" onClick={handleDelete}>
                <DeleteIcon className="h-4 w-4" />
                Delete
              </Button>
            </>
          ) : undefined
        }
      />

      <PageContainer data-testid="schedule-page-container">
        {loading && <LoadingSkeleton data-testid="schedule-loading" rows={6} />}
        {error && (
          <InlineErrorPanel
            data-testid="schedule-error-panel"
            message={error}
            action={
              <Button variant="ghost" size="sm" onClick={() => setError(null)}>
                Dismiss
              </Button>
            }
          />
        )}
        {!loading && schedule && (
          <>
            <FormSection data-testid="schedule-section-information" title="Schedule Information">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                    Description
                  </p>
                  <p className="text-sm">{schedule.description || 'No description'}</p>
                </div>

                <div>
                  <p className="text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                    Agent
                  </p>
                  <p className="text-sm">{schedule.agentName}</p>
                </div>

                <div>
                  <p className="text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                    Schedule Type
                  </p>
                  <Chip className="text-xs" label={formatScheduleType(schedule.scheduleType)} />
                </div>

                <div>
                  <p className="text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                    Status
                  </p>
                  <Chip
                    color={schedule.isActive ? 'green' : 'default'}
                    className="text-xs"
                    label={schedule.isActive ? 'Active' : 'Inactive'}
                  />
                </div>

                {schedule.scheduleType === ScheduleTypes.Cron && schedule.cronExpression && (
                  <div>
                    <p className="text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                      Cron Expression
                    </p>
                    <p className="text-sm font-mono">
                      {schedule.cronExpression}
                    </p>
                  </div>
                )}

                {schedule.scheduleType === ScheduleTypes.OneTime && schedule.scheduledDateTime && (
                  <div>
                    <p className="text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                      Scheduled Date/Time
                    </p>
                    <p className="text-sm">{formatDate(schedule.scheduledDateTime)}</p>
                  </div>
                )}

                {schedule.scheduleType === ScheduleTypes.Interval && schedule.interval && (
                  <div>
                    <p className="text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                      Interval
                    </p>
                    <p className="text-sm">{schedule.interval}</p>
                  </div>
                )}

                <div>
                  <p className="text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                    Time Zone
                  </p>
                  <p className="text-sm">{schedule.timeZone}</p>
                </div>

                <div>
                  <p className="text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                    Next Execution
                  </p>
                  <p className="text-sm">{formatDate(schedule.nextExecutionAt)}</p>
                </div>

                <div>
                  <p className="text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                    Last Execution
                  </p>
                  <p className="text-sm">{formatDate(schedule.lastExecutionAt)}</p>
                </div>

                {schedule.maxRetries && (
                  <div>
                    <p className="text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                      Max Retries
                    </p>
                    <p className="text-sm">{schedule.maxRetries}</p>
                  </div>
                )}

                {schedule.retryDelay && (
                  <div>
                    <p className="text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                      Retry Delay
                    </p>
                    <p className="text-sm">{schedule.retryDelay}</p>
                  </div>
                )}
              </div>
            </FormSection>

            <FormSection data-testid="schedule-section-executionHistory" title="Execution History">
              <ScheduleExecutionHistory scheduleId={schedule.id} />
            </FormSection>
          </>
        )}
      </PageContainer>
    </div>
  );
};
